import { Field } from '@chakra-ui/react'
import { CreatableSelect } from 'chakra-react-select'
import { FC, useMemo, useState } from 'react'

import { IOption } from '@/types/selectOption.interface'

const MAX_KEYWORD_LENGTH = 50

interface IProps {
	keywords: string[]
	placeholder?: string
	isLoading?: boolean
	onAddKeyword: (keyword: string) => void
	onRemoveKeyword: (keyword: string) => void
}

const KeywordSearchInput: FC<IProps> = ({
	keywords,
	placeholder = 'Key words',
	isLoading,
	onAddKeyword,
	onRemoveKeyword
}) => {
	const [inputValue, setInputValue] = useState('')
	const [keywordError, setKeywordError] = useState('')

	const value = useMemo<IOption[]>(() => {
		return keywords.map(keyword => ({
			label: keyword,
			value: keyword
		}))
	}, [keywords])

	const normalizeKeyword = (keyword: string) => keyword.trim().toLowerCase()

	const handleInputChange = (
		newValue: string,
		actionMeta: { action: string }
	) => {
		if (actionMeta.action !== 'input-change') {
			return inputValue
		}

		const nextValue = newValue.slice(0, MAX_KEYWORD_LENGTH)
		const normalizedValue = normalizeKeyword(nextValue)

		setInputValue(nextValue)

		if (
			normalizedValue &&
			keywords.some(keyword => normalizeKeyword(keyword) === normalizedValue)
		) {
			setKeywordError('This keyword has already been added')
		} else {
			setKeywordError('')
		}

		return nextValue
	}

	const handleChange = (newValue: IOption[] | null) => {
		if (!newValue?.length) {
			keywords.forEach(onRemoveKeyword)
			return
		}

		const nextKeywords = newValue.map(item => item.value.trim()).filter(Boolean)

		const duplicatedKeyword = nextKeywords.find((keyword, index) => {
			const normalizedKeyword = normalizeKeyword(keyword)

			return (
				nextKeywords.findIndex(
					currentKeyword =>
						normalizeKeyword(currentKeyword) === normalizedKeyword
				) !== index
			)
		})

		if (duplicatedKeyword) {
			setKeywordError('This keyword has already been added')
			return
		}

		const tooLongKeyword = nextKeywords.find(
			keyword => keyword.length > MAX_KEYWORD_LENGTH
		)

		if (tooLongKeyword) {
			setKeywordError(`Keyword can be up to ${MAX_KEYWORD_LENGTH} characters`)
			return
		}

		const addedKeyword = nextKeywords.find(
			keyword =>
				!keywords.some(
					currentKeyword =>
						normalizeKeyword(currentKeyword) === normalizeKeyword(keyword)
				)
		)

		const removedKeyword = keywords.find(
			keyword =>
				!nextKeywords.some(
					currentKeyword =>
						normalizeKeyword(currentKeyword) === normalizeKeyword(keyword)
				)
		)

		if (addedKeyword) {
			onAddKeyword(addedKeyword)
			setInputValue('')
			setKeywordError('')
			return
		}

		if (removedKeyword) {
			onRemoveKeyword(removedKeyword)
			setKeywordError('')
		}
	}

	return (
		<Field.Root invalid={!!keywordError} className='w-full'>
			<CreatableSelect
				value={value}
				inputValue={inputValue}
				onInputChange={handleInputChange}
				onChange={handleChange}
				options={[]}
				placeholder={placeholder}
				isDisabled={isLoading}
				isMulti
				isClearable={true}
				className='keyword-search'
				chakraStyles={{
					dropdownIndicator: () => ({
						display: 'none'
					}),
					menu: base => ({
						...base,
						display: 'none'
					}),
					control: (base, state) => ({
						...base,
						minHeight: '36px',
						outline: 'none',
						borderRadius: '8px',
						backgroundColor: state.isFocused ? '#CFCFCF' : '#E4E4E4',
						fontSize: '16px',
						px: state.hasValue ? '2px' : '12px',
						'&:hover': {
							backgroundColor: '#CFCFCF'
						}
					}),
					clearIndicator: base => ({
						...base,
						display: 'none',
						color: '#8B8B8B',
						'.keyword-search:hover &': {
							display: 'flex'
						}
					}),
					multiValue: base => ({
						...base,
						display: 'flex',
						alignItems: 'end',
						borderRadius: '8px',
						px: '8px',
						py: '6px',
						backgroundColor: '#707070',
						color: 'white',
						marginRight: '2px',
						'& > span:last-of-type': {
							display: 'none'
						},
						'&:hover > span:last-of-type': {
							display: 'flex',
							w: '16px',
							h: '16px'
						}
					}),
					multiValueLabel: base => ({
						...base,
						fontSize: '16px'
					}),
					input: base => ({
						...base,
						minWidth: '50px'
					})
				}}
			/>

			{keywordError && (
				<Field.ErrorText className='mt-1 rounded-[6px] bg-red-50 px-2 py-1 !text-[12px] leading-[16px] text-red-500'>
					{keywordError}
				</Field.ErrorText>
			)}
		</Field.Root>
	)
}

export default KeywordSearchInput
